import { useEffect } from "react";
import cloneDeep from "lodash-es/cloneDeep";
import { gql } from "@/services/httpAdapter";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { IPendingRequest, TDealerInventoryTrade } from "../types/dealerTrades";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { HISTORY_TRANSFERS_TABLE, PENDING_TRANSFERS_TABLE, STATUS_REQUEST } from "../constants";
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";

// Fragments
import { RequestStatusFragment, VehicleFragment } from "@/DataPages/api/inventory/fragments/dealerInventory";
import { getCommissionCycleAndPayPeriodId } from "@/Common/helpers/PayPeriods";
import moment from "moment";
import { InventoryFilterContextValue, InventoryFilterItem, useInventoryContext } from "../contexts/InventoryFilterContext";
import { objectsMatchProperties } from "@/Common/helpers/objectsTs";

type TUseCreateTransferRequest = {
	locationId?: string;
};

const query = gql`
	subscription onTransferChangeEvent($originLocationId: String, $destinationLocationId: String) {
		onTransferChangeEvent(originLocationId: $originLocationId, destinationLocationId: $destinationLocationId) {
			destinationLocationId
			event
			originLocationId
			transferRequests {
				_id
				requestId
				swapId
				originLocationId
				destinationLocationId
				payPeriodId
				commissionCycleId
				requestedByLocationId
				isBlocked
				requestStatus {
					...RequestStatus
				}
				transferType
				vehicle {
					...Vehicle
				}
			}
		}
	}
	${RequestStatusFragment}
	${VehicleFragment}
`;

const createCB = ({
	result,
	locationId,
	commissionCycles,
	queryClient,
	transferRequests,
}: {
	result: IPendingRequest;
	locationId: string;
	commissionCycles: ICommissionCycle[];
	queryClient: QueryClient;
	transferRequests: TDealerInventoryTrade[];
}) => {
	for (const element of transferRequests) {
		const { requestStatus } = element;
		const createdDate = requestStatus.createdDate;
		const { commissionCycleId, payPeriodId } = getCommissionCycleAndPayPeriodId({ date: createdDate, commissionCycles });
		queryClient.setQueriesData(
			{ queryKey: ["inventory", "pendingRequest", locationId, commissionCycleId, payPeriodId] },
			(oldData?: IPendingRequest) => {
				if (!oldData) {
					return oldData;
				}
				const resultIds = result.transferRequests.map((item) => item._id);
				const clonedOldData = cloneDeep(oldData);
				return {
					...clonedOldData,
					transferRequests: [
						...clonedOldData.transferRequests.filter((item) => {
							return !resultIds.includes(item._id);
						}),
						...result.transferRequests,
					],
				};
			}
		);
	}
};

const statusChangeCB = ({
	result,
	locationId,
	commissionCycles,
	queryClient,
	transferRequests,
	inventoryContext,
}: {
	result: IPendingRequest;
	locationId: string;
	commissionCycles: ICommissionCycle[];
	queryClient: QueryClient;
	transferRequests: TDealerInventoryTrade[];
	inventoryContext: InventoryFilterContextValue;
}) => {
	const hasPendingPart = transferRequests.some((item) => isPending(item.requestStatus.status));

	const maxDate = transferRequests.reduce((acc, item) => {
		const statusDate = moment(item.requestStatus.statusDate);
		const requestedDate = moment(item.requestStatus.requestedDate);
		return moment.max([acc, statusDate, requestedDate]);
	}, moment());

	const { commissionCycleId, payPeriodId } = getCommissionCycleAndPayPeriodId({
		date: maxDate,
		commissionCycles,
	});

	let selectedIntoHistory: InventoryFilterItem[] = [];

	if (!hasPendingPart) {
		selectedIntoHistory = inventoryContext.filterItems.filter((item) =>
			result.transferRequests.some((transferRequest) =>
				objectsMatchProperties(
					{
						table: PENDING_TRANSFERS_TABLE,
						_id: transferRequest._id,
						commissionCycleId,
						payPeriodId,
					},
					item,
					["_id", "table", "commissionCycleId", "payPeriodId"]
				)
			)
		);
	}

	inventoryContext.removeManyFromFilter(selectedIntoHistory);
	inventoryContext.addManyToFilter(selectedIntoHistory.map((item) => ({ ...item, table: HISTORY_TRANSFERS_TABLE })));

	for (const element of transferRequests) {
		const { _id } = element;
		queryClient.setQueriesData(
			{ queryKey: ["inventory", "pendingRequest", locationId, commissionCycleId, payPeriodId] },
			(oldData?: IPendingRequest) => {
				if (!oldData) {
					return oldData;
				}
				const clonedOldData = cloneDeep(oldData);
				const filteredClonedTransferRequests = clonedOldData.transferRequests.filter((item) => {
					return item._id !== _id;
				});
				const finalData = hasPendingPart ? [...filteredClonedTransferRequests, element] : filteredClonedTransferRequests;
				return {
					...clonedOldData,
					transferRequests: finalData,
				};
			}
		);
		queryClient.setQueriesData(
			{ queryKey: ["inventory", "historyRequest", locationId, commissionCycleId, payPeriodId] },
			(oldData?: IPendingRequest) => {
				if (!oldData) {
					return oldData;
				}
				const resultIds = result.transferRequests.map((item) => item._id);
				const clonedOldData = cloneDeep(oldData);
				return {
					...clonedOldData,
					transferRequests: [
						...clonedOldData.transferRequests.filter((item) => {
							return !resultIds.includes(item._id);
						}),
						...(!hasPendingPart ? result.transferRequests : []),
					],
				};
			}
		);
	}
};

const eventCallBack = ({
	result,
	queryClient,
	locationId,
	inventoryContext,
}: {
	result: IPendingRequest;
	queryClient: QueryClient;
	locationId: string;
	inventoryContext: InventoryFilterContextValue;
}) => {
	const event = result?.event;
	const commissionCycles = queryClient.getQueryData(["commissionCycles", locationId]) as ICommissionCycle[];

	const transferRequests = result.transferRequests;
	const eventMap: Record<string, Function> = {
		create: () => createCB({ result, locationId, commissionCycles, queryClient, transferRequests }),
		statusChange: () => statusChangeCB({ result, locationId, commissionCycles, queryClient, transferRequests, inventoryContext }),
	};
	const updater = eventMap[event];

	if (updater) {
		updater();
	}
};

const isPending = (status: string) => {
	return [
		STATUS_REQUEST.PENDING_ARRIVAL_APPROVAL,
		STATUS_REQUEST.PENDING_ARRIVAL,
		STATUS_REQUEST.AVAILABLE_FOR_TRADE,
		STATUS_REQUEST.PENDING_INVENTORY,
		STATUS_REQUEST.PENDING,
	].includes(status);
};

const useOnTransferChangeEventSubscription = (params: TUseCreateTransferRequest) => {
	const { locationId = "" } = params;
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();

	const transferChangeEventSubscription = getSubscription(["onTransferChangeEvent", locationId]);
	const inventoryContext = useInventoryContext();

	transferChangeEventSubscription?.subscriptions?.originTransferChangeEvent?.setCallback(
		({ data: { onTransferChangeEvent } }: { data: { onTransferChangeEvent: IPendingRequest } }) =>
			eventCallBack({ result: onTransferChangeEvent, queryClient, locationId, inventoryContext })
	);
	transferChangeEventSubscription?.subscriptions?.destinationTransferChangeEvent?.setCallback(
		({ data: { onTransferChangeEvent } }: { data: { onTransferChangeEvent: IPendingRequest } }) =>
			eventCallBack({ result: onTransferChangeEvent, queryClient, locationId, inventoryContext })
	);

	useEffect(() => {
		addSubscription(
			["onTransferChangeEvent", locationId],
			[
				{
					query,
					context: "originTransferChangeEvent",
					variables: {
						originLocationId: locationId,
					},
				},
				{
					query,
					context: "destinationTransferChangeEvent",
					variables: {
						destinationLocationId: locationId,
					},
				},
			]
		);
		return () => {};
	}, [locationId]);
};

export default useOnTransferChangeEventSubscription;
